import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowUpRight, Building2, ExternalLink, Search, Users, Wand2 } from 'lucide-react'
import { Badge, Bone, Button, Input, Panel, SegmentedControl } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import { EmptyState } from '@/components/common/EmptyState'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { usePermissions } from '@/hooks/usePermissions'
import { cn } from '@/lib/cn'
import type { Company } from '@/types/company.types'
import type { OrganizationDashboardCompany } from '@/types/dashboard.types'
import { useSetupDrafts } from '@/features/setup-wizard/hooks/useSetupWizard'
import { ResumeSetupBanner } from '@/features/setup-wizard/components/ResumeSetupBanner'
import { draftSetupPath, sortDraftsByRecency } from '@/features/setup-wizard/utils/setupDraft'
import { useDashboardStats } from '@/features/dashboard/hooks/useDashboardStats'
import { useDashboardDate } from '@/features/dashboard/components/sections/useDashboardDate'
import {
  ATTENDANCE_GOOD,
  ATTENDANCE_WARN,
  attendanceTone,
  formatNumber,
} from '@/features/dashboard/components/sections/dashboardFormat'
import { useCompanyNavList } from '../hooks/useCompanyNavList'

const PAGE_SIZE_OPTIONS = [6, 12, 24, 48]
const DEFAULT_PAGE_SIZE = 12

type CompanyFilter = 'all' | 'attention'

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  suspended: 'warning',
  inactive: 'warning',
  archived: 'neutral',
}

// Monogram tints for company tiles, picked by a hash of the full name so
// "Demo Company 01" and "Demo Company 02" don't share a colour.
const MONOGRAM_TINTS = [
  'bg-primary/10 text-primary dark:bg-sky-400/15 dark:text-sky-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300',
  'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300',
  'bg-cyan-100 text-cyan-800 dark:bg-cyan-400/15 dark:text-cyan-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-300',
  'bg-slate-200 text-slate-700 dark:bg-slate-400/15 dark:text-slate-300',
]

function monogramTint(name: string): string {
  let h = 0
  for (const char of name) h = (h * 31 + char.charCodeAt(0)) | 0
  return MONOGRAM_TINTS[Math.abs(h) % MONOGRAM_TINTS.length]
}

/** "Demo Company 01" → "DC", "247 Cars" → "2C": first character of the first two words. */
function companyInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
}

function barColor(pct: number): string {
  if (pct >= ATTENDANCE_GOOD) return 'bg-primary dark:bg-[#4aa3c4]'
  if (pct >= ATTENDANCE_WARN) return 'bg-amber-500'
  return 'bg-accent'
}

function needsAttention(stats: OrganizationDashboardCompany | undefined): boolean {
  if (!stats) return false
  return (
    stats.exceptionCount > 0 ||
    (stats.attendancePct != null && stats.attendancePct < ATTENDANCE_WARN)
  )
}

function CompanyTile({
  company,
  stats,
  statsLoading,
}: {
  company: Company
  stats: OrganizationDashboardCompany | undefined
  statsLoading: boolean
}): React.ReactElement {
  const members = stats?.memberCount ?? company.memberCount
  const pct = stats?.attendancePct ?? null
  const issues = stats?.exceptionCount ?? 0
  const isActive = company.status === 'active'

  return (
    <Link
      to={`/companies/${company._id}`}
      className={cn(
        'group relative flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-[box-shadow,border-color,transform] duration-150 ease-out',
        'hover:-translate-y-px hover:border-gray-300 hover:shadow-[var(--shadow-panel-raised)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        'dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600',
        !isActive && 'opacity-75'
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold',
            monogramTint(company.name)
          )}
        >
          {companyInitials(company.name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-primary dark:text-gray-100 dark:group-hover:text-sky-300">
            {company.name}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-gray-500 dark:text-gray-400">
            {company.code ? <span>{company.code}</span> : null}
            {company.code && members != null ? <span aria-hidden>·</span> : null}
            {members != null ? (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3 w-3" aria-hidden />
                {formatNumber(members)}
              </span>
            ) : null}
          </p>
        </div>
        {isActive ? (
          <ArrowUpRight
            className="h-4 w-4 shrink-0 text-gray-300 transition-[color,transform] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary dark:text-gray-600 dark:group-hover:text-sky-300"
            aria-hidden
          />
        ) : (
          <Badge
            variant={STATUS_VARIANT[company.status] ?? 'neutral'}
            size="sm"
            className="capitalize"
          >
            {company.status}
          </Badge>
        )}
      </div>

      {statsLoading ? (
        <Bone className="h-6 w-full rounded-md" />
      ) : stats ? (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-gray-500 dark:text-gray-400">
              {pct === null ? (
                'Nobody due yet'
              ) : (
                <>
                  <span className={cn('font-semibold tabular-nums', attendanceTone(pct))}>
                    {pct}%
                  </span>{' '}
                  attendance
                </>
              )}
            </span>
            <span className="flex items-center gap-2 tabular-nums">
              {stats.clockedInNow > 0 ? (
                <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                  {stats.clockedInNow} in
                </span>
              ) : null}
              {issues > 0 ? (
                <span className="rounded-full bg-accent/10 px-1.5 py-0.5 font-semibold text-[#d92d50] dark:bg-rose-400/15 dark:text-rose-300">
                  {issues} {issues === 1 ? 'issue' : 'issues'}
                </span>
              ) : null}
            </span>
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
            {pct !== null ? (
              <div
                className={cn('h-full rounded-full', barColor(pct))}
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {isActive ? 'No activity yet' : 'Not counted on the dashboard'}
        </p>
      )}
    </Link>
  )
}

/**
 * Dashboard card: every company the viewer can reach, each with today's
 * attendance, who is in and open issues, from the dashboard data already on
 * the page.
 */
export function DashboardCompaniesCard(): React.ReactElement {
  const navigate = useNavigate()
  const { data: companies = [], isLoading } = useCompanyNavList()
  const { can, isLoading: permissionsLoading } = usePermissions()
  const { date } = useDashboardDate()
  const dashboard = useDashboardStats({ date, enabled: can('companies', 'read') })
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<CompanyFilter>('all')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)

  const canStartCompanySetup =
    !permissionsLoading &&
    can('companies', 'create') &&
    can('import_setup', 'read') &&
    can('import_setup', 'update')
  const { data: setupDrafts = [] } = useSetupDrafts({ enabled: canStartCompanySetup })
  const latestDraft = useMemo(() => sortDraftsByRecency(setupDrafts)[0] ?? null, [setupDrafts])

  const statsById = useMemo(
    () => new Map((dashboard.data?.companyBreakdown ?? []).map((c) => [c.companyId, c])),
    [dashboard.data?.companyBreakdown]
  )
  const attentionCount = useMemo(
    () => companies.filter((company) => needsAttention(statsById.get(company._id))).length,
    [companies, statsById]
  )

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase()
    return companies.filter((company) => {
      if (filter === 'attention' && !needsAttention(statsById.get(company._id))) return false
      if (!q) return true
      return (
        company.name.toLowerCase().includes(q) || (company.code?.toLowerCase().includes(q) ?? false)
      )
    })
  }, [companies, search, filter, statsById])

  // Back to page 1 when the filter or search changes (adjusted during render, not in an effect).
  const filterKey = `${search}|${filter}|${limit}`
  const [pageFilterKey, setPageFilterKey] = useState(filterKey)
  if (pageFilterKey !== filterKey) {
    setPageFilterKey(filterKey)
    setPage(1)
  }
  const total = filteredCompanies.length
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const currentPage = Math.min(page, totalPages)
  const pagedCompanies = filteredCompanies.slice((currentPage - 1) * limit, currentPage * limit)
  const hasSearch = search.trim().length > 0

  const setupButton = canStartCompanySetup ? (
    <Button
      type="button"
      variant={latestDraft ? 'primary' : 'secondary'}
      size="sm"
      onClick={() =>
        navigate(latestDraft ? draftSetupPath(latestDraft) : '/companies/setup-wizard/drafts/new')
      }
    >
      <Wand2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="ml-1">{latestDraft ? 'Resume setup' : 'Setup'}</span>
    </Button>
  ) : null

  return (
    <Panel
      title={
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary dark:text-sky-300" aria-hidden />
          <h3 className="text-sm font-semibold tracking-[-0.006em] text-gray-900 dark:text-gray-100">
            Companies
          </h3>
          {!isLoading && companies.length > 0 ? (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {companies.length}
            </span>
          ) : null}
        </div>
      }
      action={
        <div className="flex items-center gap-3">
          <Link
            to="/companies"
            className="hidden items-center gap-1 text-xs font-medium text-primary hover:underline dark:text-sky-300 sm:inline-flex"
          >
            View all
            <ExternalLink className="h-3 w-3" aria-hidden />
          </Link>
          {setupButton}
        </div>
      }
      contentClassName="space-y-4"
    >
      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Bone key={i} className="h-26 rounded-xl" />
          ))}
        </div>
      ) : companies.length === 0 ? (
        <div className="space-y-3">
          <ResumeSetupBanner drafts={setupDrafts} variant="compact" />
          <EmptyState
            icon={Building2}
            title="No companies yet"
            description={
              latestDraft
                ? 'Your company setup is saved as a draft — continue it above to launch your first company.'
                : 'Start setup to create your first company and prepare its operations.'
            }
            action={
              canStartCompanySetup && !latestDraft ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/companies/setup-wizard/drafts/new')}
                >
                  <Wand2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Start setup
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <>
          <ResumeSetupBanner drafts={setupDrafts} variant="compact" />
          <div className="flex flex-wrap items-center gap-2">
            {dashboard.data ? (
              <SegmentedControl
                options={[
                  { value: 'all' as const, label: `All ${companies.length}` },
                  { value: 'attention' as const, label: `Needs attention ${attentionCount}` },
                ]}
                value={filter}
                onChange={setFilter}
                ariaLabel="Filter companies"
                size="sm"
              />
            ) : null}
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                aria-hidden
              />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or code…"
                aria-label="Search companies"
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>

          {filteredCompanies.length === 0 ? (
            <EmptyState
              icon={Building2}
              title={
                filter === 'attention' && !hasSearch
                  ? 'Every company looks healthy'
                  : 'No companies match'
              }
              description={
                filter === 'attention' && !hasSearch
                  ? 'No open issues and attendance is above the watch line everywhere.'
                  : 'Try a different company name or code.'
              }
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {pagedCompanies.map((company) => (
                <CompanyTile
                  key={company._id}
                  company={company}
                  stats={statsById.get(company._id)}
                  statsLoading={dashboard.isLoading}
                />
              ))}
            </div>
          )}

          {total > limit || limit !== DEFAULT_PAGE_SIZE ? (
            <TablePaginationBar
              page={currentPage}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onLimitChange={setLimit}
              leadingContent={
                hasSearch ? (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="text-xs font-medium text-primary underline-offset-2 hover:underline"
                  >
                    Clear search
                  </button>
                ) : undefined
              }
              className="pt-1"
            />
          ) : null}
        </>
      )}
    </Panel>
  )
}

DashboardCompaniesCard.displayName = 'DashboardCompaniesCard'
